import * as React from 'react';
import { Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Avatar from '@mui/material/Avatar';
import Skeleton from '@mui/material/Skeleton';
import { alpha } from '@mui/material/styles';

import PageTemplate from '../../components/PageTemplate';
import CardSection from '../../components/CardSection';

import { useAuth } from '../../../context/Auth.context';
import { useApp } from '../../../context';
import { getFollowedProviders } from '../../../utils/follows';

/** Initials for the operator's mark. Never blank. */
function initialsOf(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '·';
  return parts.slice(0, 2).map((p) => p[0].toUpperCase()).join('');
}

/**
 * The operators this traveller follows, and what they have listed.
 *
 * THE REASON THE FOLLOW BUTTON IS WORTH HAVING. Without somewhere to see the
 * result, following records a preference nobody can look at — the button would
 * be a bookmark that discards the bookmark.
 *
 * Listings come from the context's snapshot rather than a query per operator:
 * the traveller app already streams every published listing for the home feed,
 * so filtering it here costs nothing and stays live as the feed updates.
 */
export default function Following() {
  const { user } = useAuth();
  const { serviceListing, listingsLoaded } = useApp();

  const [follows, setFollows] = React.useState([]);
  const [loaded, setLoaded] = React.useState(false);

  React.useEffect(() => {
    let live = true;

    if (!user?.uid) {
      setFollows([]);
      setLoaded(true);
      return undefined;
    }

    setLoaded(false);
    getFollowedProviders(user.uid).then((next) => {
      if (!live) return;
      setFollows(next);
      setLoaded(true);
    });

    return () => { live = false; };
  }, [user?.uid]);

  const listingsByProvider = React.useMemo(() => {
    const map = {};
    (serviceListing || []).forEach((listing) => {
      const providerId = listing?.company?.id || listing?.serviceProviderId;
      if (!providerId) return;
      (map[providerId] ||= []).push(listing);
    });
    return map;
  }, [serviceListing]);

  if (!user) {
    return (
      <PageTemplate>
        <Toolbar />
        <Box sx={{ textAlign: 'center', px: 3, py: 10 }}>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 700, mb: 1 }}>
            Sign in to see who you follow
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            Following an operator keeps their places together in one list.
          </Typography>
          <Button component={Link} to="/" variant="contained">
            Back to Vhakacha
          </Button>
        </Box>
      </PageTemplate>
    );
  }

  return (
    <PageTemplate>
      <Toolbar />
      <Box sx={{ px: { xs: 2, sm: 3 }, pt: 3 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 800, letterSpacing: '-0.02em' }}>
          Following
        </Typography>
        <Typography color="text.secondary" sx={{ mt: 0.5 }}>
          Operators you follow, and what they have listed.
        </Typography>
      </Box>

      {!loaded && (
        <Box sx={{ px: { xs: 2, sm: 3 }, py: 3 }}>
          <Skeleton variant="rounded" height={72} sx={{ mb: 1.5 }} />
          <Skeleton variant="rounded" height={72} />
        </Box>
      )}

      {loaded && follows.length === 0 && (
        <Box sx={{ textAlign: 'center', px: 3, py: 8 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
            You are not following anyone yet
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            Open a listing and use Follow beside the operator&apos;s name.
          </Typography>
          <Button component={Link} to="/" variant="contained">
            Browse listings
          </Button>
        </Box>
      )}

      {loaded &&
        follows.map((follow) => {
          const listings = listingsByProvider[follow.providerId] || [];

          return (
            <Box key={follow.providerId} sx={{ mt: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: { xs: 2, sm: 3 }, mb: 1 }}>
                <Avatar
                  variant="rounded"
                  sx={{
                    width: 40,
                    height: 40,
                    fontWeight: 800,
                    fontSize: '0.85rem',
                    color: 'primary.main',
                    backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.16),
                  }}
                >
                  {initialsOf(follow.providerName)}
                </Avatar>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                    {follow.providerName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {/* Distinguishes "this operator has nothing published" from
                        "the listings have not arrived yet", which look
                        identical if both say zero. */}
                    {!listingsLoaded
                      ? 'Loading listings…'
                      : `${listings.length} ${listings.length === 1 ? 'listing' : 'listings'}`}
                  </Typography>
                </Box>
              </Box>

              {listingsLoaded && listings.length > 0 && (
                <CardSection title="" serviceProviders={listings} />
              )}

              {listingsLoaded && listings.length === 0 && (
                <Typography color="text.secondary" sx={{ px: { xs: 2, sm: 3 }, pb: 1 }}>
                  Nothing published right now. You will see it here when they list something.
                </Typography>
              )}
            </Box>
          );
        })}
    </PageTemplate>
  );
}
